import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText, Truck, BadgePercent, ShieldCheck, Leaf } from "lucide-react";

const HERO_IMG = "/generated/hero-warehouse.png";

const lineReveal = {
  hidden: { y: "112%" },
  visible: (i) => ({ y: 0, transition: { duration: 1.0, ease: [0.83, 0, 0.17, 1], delay: 0.2 + i * 0.12 } }),
};
const Line = ({ children, i }) => (
  <div className="overflow-hidden py-1">
    <motion.div custom={i} variants={lineReveal} initial="hidden" animate="visible">{children}</motion.div>
  </div>
);

const badges = [
  { icon: Truck, label: "Nationwide delivery" },
  { icon: BadgePercent, label: "Wholesale pricing" },
  { icon: Leaf, label: "Sustainable range" },
];

export const Hero = () => (
  <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
    <div className="absolute inset-0 z-0">
      <img src={HERO_IMG} alt="VR packaging distribution warehouse" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-vr-navy/95 via-vr-navy/80 to-vr-navy/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-vr-navy/70 to-transparent" />
    </div>

    <div className="relative z-10 max-w-[1500px] w-full mx-auto px-6 md:px-10 pt-32 pb-16">
      <div className="max-w-3xl">
        <Line i={0}>
          <span className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-vr-hygiene">
            Wholesale Packaging & Hygiene · New Zealand
          </span>
        </Line>
        <h1 className="mt-4 font-hero font-semibold tracking-tight leading-[1.02] text-white text-[9vw] sm:text-5xl lg:text-[4.4rem]">
          <Line i={1}>Packaging & Hygiene</Line>
          <Line i={2}>Solutions for</Line>
          <Line i={3}><span className="text-vr-hygiene">New Zealand</span> Businesses.</Line>
        </h1>

        <Line i={4}>
          <p className="mt-6 text-white/80 text-base md:text-lg max-w-xl leading-relaxed">
            One trusted supplier for cafés, restaurants, manufacturers, supermarkets and healthcare —
            foodservice packaging, hygiene, cleaning and safety, delivered nationwide at wholesale prices.
          </p>
        </Line>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }} className="mt-9 flex flex-wrap gap-4">
          <Link to="/contact" data-testid="hero-quote-cta" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors">
            Request a Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/trade-account" data-testid="hero-trade-cta" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-vr-navy font-semibold hover:bg-vr-hygiene transition-colors">
            <FileText size={18} /> Open a Trade Account
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.7 }} className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
          {badges.map((b) => (
            <div key={b.label} className="flex items-center gap-2 text-sm text-white/85">
              <b.icon size={17} className="text-vr-hygiene" /> {b.label}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);
